import { createContext, Fragment, useCallback, useContext, useState, type ReactNode } from "react"
import { caps as greekCaps } from "./greek"

/**
 * HQ's languages: English (the source) and Greek.
 *
 * The English text in the code IS the key: `t("Orders")` shows "Orders" in English and looks the
 * Greek up in src/i18n/el. A string with no Greek yet simply shows in English, so nothing ever
 * breaks — and src/i18n/coverage.test.ts fails the build when one is missing.
 *
 *  - t("Text", { name })               a sentence, with {name} filled in
 *  - t.n(count, "{n} order", "{n} orders")  one / many (Greek has the same two forms)
 *  - tc("status", "Open")              the same English word meaning two things (gettext's context)
 *  - t.rich("Open <b>HQ</b> in Safari", { b: (text) => <strong>{text}</strong> })  words wrapped in elements
 *  - tk("Text")                        marks a string kept in a table for t() later (no lookup)
 *  - ts(message)                       a sentence the SERVER wrote (errors, alerts): exact, then by template
 *
 * The language is a module-level setting, so t() needs no hook. Changing it remounts the app under
 * LocaleProvider, so every screen re-renders in the new language at once.
 */

export type Locale = "en" | "el"
export type Plural = readonly [one: string, other: string]
export type Dictionary = Record<string, string | Plural>
export type Params = Record<string, string | number | null | undefined>
type Tags = Record<string, (text: string) => ReactNode>

export const LANGUAGES: ReadonlyArray<{ code: Locale; name: string }> = [
  { code: "en", name: "English" },
  { code: "el", name: "Ελληνικά" },
]

let current: Locale = "en"
let dictionary: Dictionary = {}
let serverTemplates: Template[] = []
const serverCache = new Map<string, string>()

/** The language HQ is showing now. */
export const getLocale = (): Locale => current
/** The Intl locale for numbers and dates (en-GB writes 29 Sep and 18:00; el-GR 29 Σεπ and 1.234,50 €). */
export const intlLocale = (): string => (current === "el" ? "el-GR" : "en-GB")

// ---------------------------------------------------------------------------------------------
// Looking text up

function lookup(key: string): string | Plural | undefined {
  return current === "en" ? undefined : dictionary[key]
}

function fill(text: string, params?: Params): string {
  if (!params) return text
  return text.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = params[name]
    return value === undefined || value === null ? match : String(value)
  })
}

/**
 * A plain number as this language writes it: 1,234.5 / 1.234,5. For counts inside sentences; order
 * numbers and years are NOT numbers to format (#1668 must not become #1.668), so pass them as text.
 */
export function formatCount(value: number): string {
  return new Intl.NumberFormat(intlLocale(), { maximumFractionDigits: 2 }).format(value)
}

type Translate = {
  (key: string, params?: Params): string
  /** One / many: t.n(3, "{n} order", "{n} orders"). {n} is the count, written the language's way. */
  n: (count: number, one: string, other: string, params?: Params) => string
  /** Words wrapped in elements: t.rich("Open <b>HQ</b> now", { b: (s) => <strong>{s}</strong> }). */
  rich: (key: string, tags: Tags, params?: Params) => ReactNode
}

export const t: Translate = Object.assign(
  (key: string, params?: Params): string => {
    const entry = lookup(key)
    return fill(typeof entry === "string" ? entry : key, params)
  },
  {
    n: (count: number, one: string, other: string, params?: Params): string => {
      const entry = lookup(`${one}|${other}`)
      const [singular, plural] = Array.isArray(entry) ? entry : [one, other]
      const form = new Intl.PluralRules(current).select(count) === "one" ? singular : plural
      return fill(form, { n: formatCount(count), ...params })
    },
    rich: (key: string, tags: Tags, params?: Params): ReactNode => {
      const entry = lookup(key)
      const text = fill(typeof entry === "string" ? entry : key, params)
      const parts: ReactNode[] = []
      const pattern = /<(\w+)>([\s\S]*?)<\/\1>/g
      let last = 0
      let match: RegExpExecArray | null
      while ((match = pattern.exec(text))) {
        if (match.index > last) parts.push(text.slice(last, match.index))
        const render = tags[match[1]]
        parts.push(<Fragment key={parts.length}>{render ? render(match[2]) : match[2]}</Fragment>)
        last = pattern.lastIndex
      }
      if (last < text.length) parts.push(text.slice(last))
      return <>{parts}</>
    },
  }
)

/** The same English word meaning two things: tc("status", "Open") vs tc("action", "Open"). */
export function tc(context: string, key: string, params?: Params): string {
  const entry = lookup(`${context}::${key}`)
  return fill(typeof entry === "string" ? entry : key, params)
}

/** Marks a string kept in a table (labels by status, nav items) for t() at render. Returns it as-is. */
export function tk<T extends string>(key: T): T {
  return key
}

/**
 * A table whose values are English text: reading a value gives it in the current language, so code
 * like DELIVERY_LABEL[method] needs no change. With a context, its words are looked up as
 * tc(context, value) — for tables whose words mean something else elsewhere ("Done" as a queue).
 * The values are found by the coverage test.
 */
export function localized<T extends Record<string, string>>(table: T, context?: string): T {
  return new Proxy(table, {
    get(target, property, receiver) {
      const value = Reflect.get(target, property, receiver)
      if (typeof value !== "string") return value
      return context ? tc(context, value) : t(value)
    },
  })
}

/** Upper case as the language writes it: Greek capitals carry no accents (ΠΑΡΑΓΓΕΛΙΕΣ, not ΠΑΡΑΓΓΕΛΊΕΣ). */
export function caps(text: string): string {
  return greekCaps(text)
}

// ---------------------------------------------------------------------------------------------
// Sentences the server wrote

/**
 * A server template: its English with {placeholders}, and the Greek. A placeholder may carry a kind,
 * {amount:money}, which says how to read the value and write it again in Greek:
 *  - (none)  any text, kept as it is (a name, an order number, a product title)
 *  - n       a number written the English way (1,234.5), rewritten the Greek way (1.234,5)
 *  - money   an amount the server wrote as €1,234.50, rewritten as 1.234,50 €
 *  - t       a phrase that is itself translated (a status, a method)
 */
type Template = { pattern: RegExp; names: Array<{ name: string; kind: string }>; greek: string; literal: number }

const KIND_PATTERN: Record<string, string> = {
  n: "-?\\d[\\d,]*(?:\\.\\d+)?",
  money: "-?€\\s?-?\\d[\\d,]*(?:\\.\\d+)?|-?\\d[\\d,]*(?:\\.\\d+)?\\s?€",
  // A brand or a name: words only — no separators or digits, so it never swallows a list or a count.
  brand: "[^·;:.,0-9]+?",
}

function compile(english: string, greek: string): Template {
  const names: Template["names"] = []
  let source = ""
  let literal = 0
  let last = 0
  const placeholder = /\{(\w+)(?::(\w+))?\}/g
  let match: RegExpExecArray | null
  while ((match = placeholder.exec(english))) {
    const text = english.slice(last, match.index)
    source += escapeRegExp(text)
    literal += text.length
    const kind = match[2] ?? "text"
    names.push({ name: match[1], kind })
    source += `(${KIND_PATTERN[kind] ?? "[\\s\\S]+?"})`
    last = placeholder.lastIndex
  }
  const tail = english.slice(last)
  source += escapeRegExp(tail)
  literal += tail.length
  return { pattern: new RegExp(`^${source}$`), names, greek, literal }
}

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

const englishNumber = (text: string) => Number(text.replace(/[€\s,]/g, ""))

function rewrite(value: string, kind: string): string {
  if (kind === "n") return formatCount(englishNumber(value))
  if (kind === "money") {
    const amount = englishNumber(value)
    return new Intl.NumberFormat(intlLocale(), { style: "currency", currency: "EUR", minimumFractionDigits: value.includes(".") ? 2 : 0, maximumFractionDigits: 2 }).format(amount)
  }
  if (kind === "t" || kind === "brand") return ts(value)
  return value
}

/** One server sentence: word for word, else the most specific template it fits; null when neither. */
function translateServer(message: string): string | null {
  const exact = dictionary[message]
  if (typeof exact === "string") return exact
  for (const template of serverTemplates) {
    const match = template.pattern.exec(message)
    if (!match) continue
    const values: Record<string, string> = {}
    template.names.forEach((placeholder, index) => {
      values[placeholder.name] = rewrite(match[index + 1], placeholder.kind)
    })
    return template.greek.replace(/\{(\w+)\}/g, (whole, name: string) => values[name] ?? whole)
  }
  return null
}

/**
 * A message the server wrote in English (a refusal, an alert, a check), in the current language: the
 * whole sentence if it is known word for word, else the first template it fits, else as it came.
 */
export function ts(message: string | null | undefined): string {
  if (!message) return ""
  if (current === "en") return message
  const cached = serverCache.get(message)
  if (cached !== undefined) return cached
  let result = translateServer(message)
  // A list the server joined with "; " (and closed with a full stop): each part on its own, and the
  // whole only if every part is known — a half-translated list reads worse than an English one.
  if (result === null && message.includes("; ")) {
    const end = message.endsWith(".") ? "." : ""
    const parts = message.slice(0, message.length - end.length).split("; ").map(translateServer)
    if (parts.every((part) => part !== null)) result = parts.join("; ") + end
  }
  if (result === null) result = message
  if (result === message && import.meta.env.DEV) {
    console.warn(`[i18n] No Greek for the server message: ${JSON.stringify(message)}`)
  }
  serverCache.set(message, result)
  return result
}

// ---------------------------------------------------------------------------------------------
// Switching

const STORAGE_KEY = "hq-locale"

type Stored = { locale: Locale; at: number }

/** This device's choice: { locale, at } — `at` decides between it and the one saved on the account. */
export function storedChoice(): Stored | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<Stored>
    return parsed.locale === "el" || parsed.locale === "en" ? { locale: parsed.locale, at: Number(parsed.at) || 0 } : null
  } catch {
    return null
  }
}

function store(choice: Stored) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(choice))
  } catch {
    // Private mode: the choice lasts until the app is closed, and the account keeps it anyway.
  }
}

/**
 * The phone's notifications are shown by the service worker (public/sw.js), which cannot see this
 * page: so the language, and the Greek for server sentences, are left for it in Cache Storage. Only
 * words are kept there — the app itself is never cached. A failure leaves notifications in English.
 */
async function shareWithNotifications(locale: Locale, server: Record<string, string> | null) {
  if (typeof caches === "undefined") return
  try {
    const cache = await caches.open("hq-i18n")
    const version = Date.now()
    if (server) await cache.put("/__hq-i18n/el.json", new Response(JSON.stringify(server), { headers: { "content-type": "application/json" } }))
    await cache.put("/__hq-i18n/locale", new Response(JSON.stringify({ locale, version }), { headers: { "content-type": "application/json" } }))
  } catch {
    // Notifications simply stay in English.
  }
}

/** Loads a language's words (Greek is its own file, fetched only by those who choose it) and switches. */
export async function activate(next: Locale): Promise<void> {
  if (next === "el") {
    const words = await import("../i18n/el")
    dictionary = { ...words.el }
    serverTemplates = Object.entries(words.elServer)
      .filter(([english]) => /\{\w+(?::\w+)?\}/.test(english))
      .map(([english, greek]) => compile(english, greek))
      // The most specific template first: the one with the most fixed words.
      .sort((a, b) => b.literal - a.literal)
    for (const [english, greek] of Object.entries(words.elServer)) {
      if (!/\{\w+(?::\w+)?\}/.test(english) && !(english in dictionary)) dictionary[english] = greek
    }
    void shareWithNotifications("el", words.elServer)
  } else {
    dictionary = {}
    serverTemplates = []
    void shareWithNotifications("en", null)
  }
  serverCache.clear()
  current = next
  if (typeof document !== "undefined") document.documentElement.lang = next
}

type LocaleState = {
  locale: Locale
  /** Switches HQ to `next` on this device; `at` is when the choice was made (for the account copy). */
  setLocale: (next: Locale, at?: number) => Promise<void>
  switching: boolean
}

const LocaleContext = createContext<LocaleState | null>(null)

/**
 * Holds the language. Its children are keyed by it, so changing language mounts the app afresh in
 * the new one (forms in progress are lost — it is changed in Settings, where nothing is half-typed).
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setState] = useState<Locale>(current)
  const [switching, setSwitching] = useState(false)

  const setLocale = useCallback(async (next: Locale, at: number = Date.now()) => {
    store({ locale: next, at })
    if (next === current) return
    setSwitching(true)
    try {
      await activate(next)
      setState(next)
    } finally {
      setSwitching(false)
    }
  }, [])

  return (
    <LocaleContext.Provider value={{ locale, setLocale, switching }}>
      <Fragment key={locale}>{children}</Fragment>
    </LocaleContext.Provider>
  )
}

export function useLocale(): LocaleState {
  const value = useContext(LocaleContext)
  if (!value) throw new Error("useLocale must be used inside <LocaleProvider>")
  return value
}
